import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { CATEGORIES } from "./DQMData";
import { getPieChartOptions } from "./chartConfig";

// Load Highcharts modules
require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/full-screen")(Highcharts);

const CategoryChart = ({
  loading,
  totalRecords,
  isDatasetSelected = false,
  categories = {},
  includeClean = true,
  showLegend = false,
}) => {
  const [newOptions, setNewOptions] = useState({
    credits: { enabled: false },
    colors: ["#777"],
    title: { align: "center", text: "FINDINGS BY CATEGORY" },
    chart: {
      height: 400,
      borderRadius: 15,
      borderWidth: 1,
      borderColor: "#ccc",
    },
  });

  const [totalCategoryFindings, setTotalCategoryFindings] = useState(0);
  const [filteredCategories, setFilteredCategories] = useState([]);

  const chartComponent = useRef(null);

  const unavailableText = useMemo(() => {
    if (totalRecords && isDatasetSelected) return "";
    return !isDatasetSelected ? "No Dataset<br />Selected" : "No Records<br />Found";
  }, [isDatasetSelected, totalRecords]);

  const updatedOptions = useMemo(
    () =>
      getPieChartOptions(Highcharts, {
        label: "FINDINGS BY CATEGORY",
        unavailableText,
        showLegend,
        ...(!!totalRecords && isDatasetSelected
          ? {
              showLabels: true,
              dataSearies: [
                ...filteredCategories,
                ...(includeClean
                  ? [
                      {
                        name: "Clean",
                        y: totalRecords - totalCategoryFindings,
                        color: "#1B5E20",
                      },
                    ]
                  : []),
              ],
            }
          : { showLabels: false }),
      }),
    [
      filteredCategories,
      includeClean,
      showLegend,
      isDatasetSelected,
      totalCategoryFindings,
      totalRecords,
      unavailableText,
    ]
  );

  useEffect(() => {
    if (!CATEGORIES.some((cat) => cat.label in categories)) return;

    let totalFindings = 0;
    const categoryWiseFindings = CATEGORIES.map((category) => {
      totalFindings += categories[category.value] || 0;
      return {
        name: category.value,
        y: categories[category.value] || 0,
        color: category.color,
      };
    });

    setTotalCategoryFindings(totalFindings);
    setFilteredCategories(includeClean ? categoryWiseFindings : categoryWiseFindings.filter(({ y }) => y > 0));
  }, [categories, includeClean, isDatasetSelected, totalRecords]);

  useEffect(() => {
    const chart = chartComponent.current?.chart;
    if (!chart || !Object.keys(chart).length) return;

    chart.showLoading("Loading...");

    if (loading) {
      chart.showLoading("Loading...");

      setNewOptions({
        credits: { enabled: false },
        chart: {
          height: 400,
          borderRadius: 15,
          borderWidth: 1,
          borderColor: "#ccc",
        },
      });
    } else {
      if (!isDatasetSelected) {
        chart.showLoading("No dataset selected");
        return;
      }

      chart.hideLoading();
      setNewOptions(updatedOptions);
    }
  }, [isDatasetSelected, loading, updatedOptions]);

  return <HighchartsReact key={includeClean} highcharts={Highcharts} ref={chartComponent} options={newOptions} />;
};

CategoryChart.propTypes = {
  loading: PropTypes.bool,
  totalRecords: PropTypes.number,
  categories: PropTypes.object,
  includeClean: PropTypes.bool,
  showLegend: PropTypes.bool,
  isDatasetSelected: PropTypes.bool,
};

export default CategoryChart;
